import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Slider,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { CheckCircle, TrendingUp, AccessTime, Psychology } from '@mui/icons-material';
import api from '../lib/api';
import { useMutation } from '@tanstack/react-query';
import { readinessQuestions, calculateReadinessScores } from '../config/readinessQuestions';

const ReadinessAssessmentPage: React.FC = () => {
  const { id: agreementId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [responses, setResponses] = useState<any>({});

  const steps = ['Capability', 'Opportunity', 'Motivation', 'Review & Submit'];

  const submitMutation = useMutation({
    mutationFn: async (assessmentData: any) => {
      const res = await api.post(`/assessments?agreementId=${agreementId}`, assessmentData);
      return res.data;
    },
    onSuccess: () => {
      navigate(`/agreements/${agreementId}`);
    }
  });

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleResponseChange = (questionId: string, value: any) => {
    const question = readinessQuestions.find(q => q.id === questionId);
    setResponses((prev: any) => ({
      ...prev,
      [questionId]: {
        questionId,
        questionText: question?.question,
        questionType: question?.questionType,
        answerValue: question?.questionType === 'SCALE' ? value : null,
        answerText: question?.questionType !== 'SCALE' ? value : null,
        domain: question?.domain,
        category: question?.category
      }
    }));
  };

  const handleSubmit = () => {
    const responsesArray = Object.values(responses);
    const scores = calculateReadinessScores(responsesArray);

    submitMutation.mutate({
      assessmentType: 'READINESS',
      ...scores,
      responses: responsesArray
    });
  };

  const getQuestionsForStep = (step: number) => {
    const domains = ['CAPABILITY', 'OPPORTUNITY', 'MOTIVATION'];
    return readinessQuestions.filter(q => q.domain === domains[step]);
  };

  const currentQuestions = getQuestionsForStep(activeStep);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Ready';
    if (score >= 60) return 'Mostly Ready';
    if (score >= 40) return 'Needs Support';
    return 'Not Ready';
  };

  if (activeStep === 3) {
    // Review step
    const scores = calculateReadinessScores(Object.values(responses));
    return (
      <Container maxWidth="md">
        <Box mt={4} mb={4}>
          <Typography variant="h4" gutterBottom>
            Review Your Readiness Assessment
          </Typography>

          {/* Score Summary */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Psychology color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h5">Overall Readiness</Typography>
                <Typography variant="body2" color="text.secondary">
                  {scores.overallScore}% - {getScoreLabel(scores.overallScore)}
                </Typography>
              </Box>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={scores.overallScore} 
              color={getScoreColor(scores.overallScore)}
              sx={{ height: 10, borderRadius: 5, mb: 3 }}
            />

            {/* Domain Scores */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <Box flex="1" minWidth="200px">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">
                    <TrendingUp sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Capability
                  </Typography>
                  <Chip 
                    label={`${scores.capabilityScore}%`} 
                    color={getScoreColor(scores.capabilityScore)} 
                    size="small"
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={scores.capabilityScore} 
                  color={getScoreColor(scores.capabilityScore)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>

              <Box flex="1" minWidth="200px">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">
                    <AccessTime sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Opportunity
                  </Typography>
                  <Chip 
                    label={`${scores.opportunityScore}%`} 
                    color={getScoreColor(scores.opportunityScore)} 
                    size="small"
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={scores.opportunityScore} 
                  color={getScoreColor(scores.opportunityScore)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>

              <Box flex="1" minWidth="200px">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">
                    <CheckCircle sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Motivation
                  </Typography>
                  <Chip 
                    label={`${scores.motivationScore}%`} 
                    color={getScoreColor(scores.motivationScore)} 
                    size="small"
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={scores.motivationScore} 
                  color={getScoreColor(scores.motivationScore)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Recommendations */}
          {(scores.capabilityScore < 80 || scores.opportunityScore < 80 || scores.motivationScore < 80) && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Consider addressing these areas before starting:
              </Typography>
              {scores.capabilityScore < 80 && (
                <Typography variant="body2">
                  • Capability: Review educational resources about trust structures
                </Typography>
              )}
              {scores.opportunityScore < 80 && (
                <Typography variant="body2">
                  • Opportunity: Ensure you have necessary resources and support
                </Typography>
              )}
              {scores.motivationScore < 80 && (
                <Typography variant="body2">
                  • Motivation: Revisit your goals and commitment level
                </Typography>
              )}
            </Alert>
          )}

          {scores.overallScore >= 80 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              You're ready to begin your trust implementation journey! 🚀
            </Alert>
          )}

          {submitMutation.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitMutation.error instanceof Error 
                ? submitMutation.error.message 
                : 'Failed to submit assessment'}
            </Alert>
          )}

          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button onClick={handleBack}>Back</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
            >
              {submitMutation.isPending ? 'Submitting...' : 'Submit Assessment'}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={4}>
        <Typography variant="h4" gutterBottom>
          Implementation Readiness Assessment
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Based on the COM-B framework, assess your readiness across three key domains
        </Typography>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Questions */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom mb={3}>
            {steps[activeStep]} Assessment
          </Typography>

          {currentQuestions.map((question) => {
            const response = responses[question.id];

            return (
              <Box key={question.id} mb={4}>
                <Typography variant="body1" gutterBottom>
                  {question.question}
                  {question.required && <span style={{ color: 'red' }}> *</span>}
                </Typography>

                {question.questionType === 'LIKERT' && (
                  <RadioGroup
                    value={response?.answerText || ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                  >
                    {question.options?.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio />}
                        label={option}
                      />
                    ))}
                  </RadioGroup>
                )}

                {question.questionType === 'YES_NO' && (
                  <RadioGroup
                    value={response?.answerText || ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                )}

                {question.questionType === 'TEXT' && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={response?.answerText || ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    placeholder="Enter your response..."
                  />
                )}
              </Box>
            );
          })}

          {/* Navigation */}
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext}>
              {activeStep === steps.length - 2 ? 'Review' : 'Next'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ReadinessAssessmentPage;

